import {
  faExclamationCircle,
  faSort,
  faSortDown,
  faSortUp
} from '@fortawesome/free-solid-svg-icons';
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Col,
  FormControl,
  InputGroup,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from 'react-bootstrap';
import {
  useAsyncDebounce,
  useFilters,
  useGlobalFilter,
  useSortBy,
  useTable
} from 'react-table';
import { useStores } from '../../stores/useStores';

const SeasonOverviewReportTable = ({
  data,
  selectedPropertyGroup,
  selectedInsectGroup,
  viewMapData,
  setViewMapData,
  propertyId
}) => {
  const { traplogStore } = useStores();
  const buttonRefs = useRef([]);

  useEffect(() => {
    const isInSelected = selectedPropertyGroup.properties.find(
      (p) => p.id === propertyId
    );
    if (buttonRefs.current[propertyId] && isInSelected) {
      buttonRefs.current[propertyId].click();
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: 'RPIN',
        accessor: 'property.identifier',
        disableSortBy: true
      },
      {
        Header: 'Orchard',
        accessor: 'property.name',
        disableSortBy: true
      },
      {
        Header: 'Production Site',
        accessor: (row) =>
          row.trappingZone.name + (row.trappingZone.cmsm ? ' (CMSM)' : ''),
        disableSortBy: true
      },
      {
        Header: 'Mating Disruption',
        accessor: (row) =>
          row.trappingZone.mating_disruption_tie
            ? row.trappingZone.mating_disruption_tie.name
            : '',
        disableSortBy: true
      },
      {
        Header: 'Sprays',
        accessor: 'counts.sprays'
      },
      {
        id: 'actionable-thresholds',
        Header: (
          <Fragment>
            Actionable Thresholds{' '}
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip>
                  Trapping thresholds that required a spray application
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className='text-danger'
              />
            </OverlayTrigger>
          </Fragment>
        ),
        accessor: 'counts.thresholdMetRecords'
      },
      {
        id: 'covered-thresholds',
        Header: (
          <Fragment>
            Covered Thresholds{' '}
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip>
                  Trapping thresholds that were covered from a previous spray
                  application
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className='text-info'
              />
            </OverlayTrigger>
          </Fragment>
        ),
        accessor: 'counts.coveredThresholdRecords'
      },
      {
        Header: 'Total Thresholds',
        accessor: (row) =>
          row.counts.thresholdMetRecords + row.counts.coveredThresholdRecords
      },
      {
        Header: 'Property Hot traps',
        accessor: (row) => (
          <Row className='m-0'>
            <Col className='p-0 text-center'>
              <Button
                ref={(el) => (buttonRefs.current[row.property.id] = el)}
                size='sm'
                variant={
                  row.trappingZone.id === traplogStore.selectedTrappingZoneId &&
                  viewMapData?.property.id // Ensure outline (unselected) when no map data.
                    ? 'primary'
                    : 'outline-primary'
                }
                disabled={
                  traplogStore.loadingTrappingZones ||
                  traplogStore.loadingTrappingResults
                }
                onClick={() => {
                  const data = { ...row, selectedInsectGroup };
                  // Select trapping zone id if not already selected.
                  if (
                    traplogStore.selectedTrappingZoneId !== data.trappingZone.id
                  ) {
                    traplogStore.setSelectedTrappingZoneId(
                      data.trappingZone.id
                    );
                  }
                  // Set map data.
                  setViewMapData(data);
                  // Reset trappingZones if different property selected.
                  if (data?.property.id !== viewMapData?.property.id) {
                    traplogStore.trappingZones = [];
                  }
                }}
              >
                View Map
              </Button>
            </Col>
          </Row>
        ),
        disableSortBy: true
      }
    ],
    [
      viewMapData?.property.id,
      traplogStore.selectedTrappingZoneId,
      traplogStore.loadingTrappingZones,
      traplogStore.loadingTrappingResults
    ]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      autoResetGlobalFilter: false
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  const { globalFilter } = state;

  const stickyRef = useRef(null);
  const [isSticky, setSticky] = useState(false);

  useEffect(() => {
    // When no map data, disable sticky.
    if (!viewMapData?.property.id) setSticky(false);
    const handleScroll = () => {
      if (
        stickyRef.current &&
        viewMapData?.property.id &&
        window.innerWidth >= 992
      ) {
        const topValue = 520;
        setSticky(stickyRef.current.getBoundingClientRect().top <= topValue);
      }
    };
    const handleResize = () => {
      // Disable sticky when window width is less than 992 (bootstrap lg).
      const width = window.innerWidth;
      if (width < 992) {
        setSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleResize);
    };
  }, [viewMapData?.property.id]);

  return (
    <Row className='bg-white'>
      <Col>
        <GlobalFilter
          filter={globalFilter}
          setFilter={setGlobalFilter}
          hasMapData={viewMapData}
        />
        <Table
          size='sm'
          bordered
          hover
          responsive={!isSticky}
          {...getTableProps()}
        >
          <thead
            ref={stickyRef}
            className={
              isSticky
                ? 'season-overview-table-sticky thead-light'
                : 'thead-light'
            }
          >
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  // Add the sorting props to control sorting. For this example
                  // we can add them into the header props
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {/* Add a sort direction indicator */}
                    <span>
                      {column.canSort ? (
                        column.isSorted ? (
                          column.isSortedDesc ? (
                            <Fragment>
                              {' '}
                              <FontAwesomeIcon icon={faSortDown} />
                            </Fragment>
                          ) : (
                            <Fragment>
                              {' '}
                              <FontAwesomeIcon icon={faSortUp} />
                            </Fragment>
                          )
                        ) : (
                          <Fragment>
                            {' '}
                            <FontAwesomeIcon icon={faSort} />
                          </Fragment>
                        )
                      ) : null}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};

const GlobalFilter = React.memo((props) => {
  const [value, setValue] = useState(props.filter);
  const onChange = useAsyncDebounce((value) => {
    props.setFilter(value || undefined);
  }, 200);

  useEffect(() => {
    if (value && !props.filter) props.setFilter(value);
  }, [value, props.filter]);

  return (
    <Row
      className={props.hasMapData ? 'season-overview-table-filter-sticky' : ''}
    >
      <Col md={0} lg={6}>
        <InputGroup className='mb-1 float-left'>
          <InputGroup.Prepend>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
          </InputGroup.Prepend>
          <FormControl
            placeholder='Search'
            aria-label='Search'
            value={value || ''}
            onChange={(e) => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
          />
          <InputGroup.Append>
            <Button
              variant='outline-secondary'
              onClick={() => {
                setValue('');
                onChange('');
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      </Col>
      <Col md={6} lg={3} />
      <Col md={6} lg={3} />
    </Row>
  );
});

SeasonOverviewReportTable.propTypes = {
  tableData: PropTypes.array,
  selectedPropertyGroup: PropTypes.object,
  selectedInsectGroup: PropTypes.object,
  viewMapData: PropTypes.object,
  setViewMapData: PropTypes.func.isRequired,
  propertyId: PropTypes.number
};

export default React.memo(observer(SeasonOverviewReportTable));
